import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useReleasedPlatforms } from '@/components/shared/platform/useReleasedPlatforms';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import {
  PLATFORM_REGISTRY,
  type PlatformId,
} from '@kirin/api-client/contracts/platform-registry';
import { Arch, detectArch, detectPlatform, Platform } from '@/lib/platform';
import { PersonalKeyControl } from '@/pages/dashboard/components/PersonalKeyControl';
import { OrganizationKeyControl } from '@/pages/organization/components/OrganizationKeyControl';
import { Download, Key } from 'lucide-react';
import { Link } from 'react-router-dom';
import { VsixDirectDownload } from '../VsixDirectDownload';
import { AgentInstallSteps } from './AgentInstallTabContent';
import { ClaudeDesktopPluginSteps } from './ClaudeDesktopInstallTabContent';
import { CursorInstallSteps } from './CursorTabContent';
import { VSCodeInstallSteps } from './VSCodeTabContent';
import {
  AgentCoverageBanner,
  AgentPrerequisiteBanner,
  InstallCardSelector,
  SetupFrame,
  type SetupCardId,
} from './InstallSetupCards';
import {
  AGENT_BASE_URL,
  CURSOR_BASE_URL,
  VSCODE_COPILOT_BASE_URL,
  overrideUrlOrEmpty,
} from './constants';

interface InstallTabProps {
  organizationId?: string;
}

/**
 * Tools the Kirin Agent covers that map to a platform-registry id. Each is
 * shown in the Agent coverage banner — highlighted once its platform release
 * flag is on (via useReleasedPlatforms), otherwise as a COMING SOON badge.
 * When adding a new agent platform, add its id here so Get Started surfaces it
 * automatically based on its feature flag. See docs/add-new-platform.md.
 */
const AGENT_PLATFORM_IDS: PlatformId[] = [
  'claude',
  'claude_desktop',
  'windsurf',
  'gemini_cli',
  'antigravity',
  'codex',
];

/** Agent-covered tools that have no platform-registry id yet — always COMING SOON. */
const AGENT_COMING_SOON_TOOLS = ['JetBrains', 'Kiro'] as const;

/**
 * Install tab component that can be used in both
 * dashboard and organization views
 */
export default function InstallTab({ organizationId }: InstallTabProps) {
  const released = useReleasedPlatforms();
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.SHOW_VSIX_DOWNLOAD,
    flagsmithFeatureFlags.HIDE_VSCODE_EXTENSION,
  ]);
  const showVsixDownload =
    flags[flagsmithFeatureFlags.SHOW_VSIX_DOWNLOAD] ?? false;
  // VSCode extension is being deprecated; the flag hides its card when true.
  const showVSCode = !(
    flags[flagsmithFeatureFlags.HIDE_VSCODE_EXTENSION] ?? false
  );
  const vsixVersion = import.meta.env.VITE_VSIX_VERSION || '';

  const [selectedCard, setSelectedCard] = useState<SetupCardId>('agent');
  // When the VSCode card is hidden but still selected (flag flipped on), fall
  // back to the Agent frame without mutating state in an effect.
  const effectiveCard: SetupCardId =
    !showVSCode && selectedCard === 'vscode' ? 'agent' : selectedCard;

  const { hasAccess: isOwner } = useRoleCheck(
    organizationId ? { param: 'organizationId', anyOf: ['owner'] } : undefined,
    organizationId
  );

  const detectedPlatform = detectPlatform();

  // Async UACH detection — only Chrome/Edge respond, and only Linux/Windows
  // ship per-arch binaries so those are the only platforms we detect for.
  // Falls back to `null` (→ amd64 default) for Safari/Firefox and headless
  // contexts. Refreshing on detectedPlatform change scopes the detection.
  const [detectedArch, setDetectedArch] = useState<Arch | null>(null);
  useEffect(() => {
    if (
      detectedPlatform !== Platform.LINUX &&
      detectedPlatform !== Platform.WIN32
    ) {
      return;
    }
    let cancelled = false;
    detectArch().then(arch => {
      if (!cancelled) setDetectedArch(arch);
    });
    return () => {
      cancelled = true;
    };
  }, [detectedPlatform]);

  const linuxDebAmd64Url = vsixVersion
    ? `${AGENT_BASE_URL}/v${vsixVersion}/kirin_${vsixVersion}_amd64.deb`
    : '';
  const linuxDebArm64Url = vsixVersion
    ? `${AGENT_BASE_URL}/v${vsixVersion}/kirin_${vsixVersion}_arm64.deb`
    : '';
  const linuxRpmAmd64Url = vsixVersion
    ? `${AGENT_BASE_URL}/v${vsixVersion}/kirin-${vsixVersion}.x86_64.rpm`
    : '';
  const linuxRpmArm64Url = vsixVersion
    ? `${AGENT_BASE_URL}/v${vsixVersion}/kirin-${vsixVersion}.aarch64.rpm`
    : '';

  const kirinAgentUrls = {
    macOS_PKG: vsixVersion
      ? `${AGENT_BASE_URL}/v${vsixVersion}/Kirin-${vsixVersion}.pkg`
      : '',
    // Per-arch NSIS installers. amd64 also runs on Windows-on-ARM via
    // emulation; arm64 is the native build for ARM devices. Both names embed
    // the version (KIR-3811) and add the arch suffix (KIR-3838).
    windows_amd64_EXE: vsixVersion
      ? `${AGENT_BASE_URL}/v${vsixVersion}/Kirin-${vsixVersion}-amd64.exe`
      : '',
    windows_arm64_EXE: vsixVersion
      ? `${AGENT_BASE_URL}/v${vsixVersion}/Kirin-${vsixVersion}-arm64.exe`
      : '',
  };

  const agentPlatformDownloadMeta = [
    {
      platform: Platform.DARWIN,
      version: vsixVersion,
      url: kirinAgentUrls.macOS_PKG,
    },
    {
      platform: Platform.WIN32,
      version: vsixVersion,
      url: kirinAgentUrls.windows_amd64_EXE,
      arch: Arch.AMD64,
    },
    {
      platform: Platform.WIN32,
      version: vsixVersion,
      url: kirinAgentUrls.windows_arm64_EXE,
      arch: Arch.ARM64,
    },
    {
      platform: Platform.LINUX,
      version: vsixVersion,
      url: linuxDebAmd64Url,
      arch: Arch.AMD64,
      format: 'deb' as const,
    },
    {
      platform: Platform.LINUX,
      version: vsixVersion,
      url: linuxDebArm64Url,
      arch: Arch.ARM64,
      format: 'deb' as const,
    },
    {
      platform: Platform.LINUX,
      version: vsixVersion,
      url: linuxRpmAmd64Url,
      arch: Arch.AMD64,
      format: 'rpm' as const,
    },
    {
      platform: Platform.LINUX,
      version: vsixVersion,
      url: linuxRpmArm64Url,
      arch: Arch.ARM64,
      format: 'rpm' as const,
    },
  ];

  // Cursor VSIX URLs from the CDN (KIR-412), with VITE_VSIX_DOWNLOAD_URL_* as
  // an optional override (e.g. Cloudflare Pages pointing at a PR prefix).
  // `overrideUrlOrEmpty` drops any override still aimed at the legacy S3 host.
  const cursorCdnUrl = (platformSlug: string): string =>
    vsixVersion
      ? `${CURSOR_BASE_URL}/v${vsixVersion}/kirin-cursor-v${vsixVersion}-${platformSlug}.vsix`
      : '';
  const vsixUrlDarwin =
    overrideUrlOrEmpty(import.meta.env.VITE_VSIX_DOWNLOAD_URL_DARWIN) ||
    cursorCdnUrl('darwin-arm64');
  const vsixUrlWin32 =
    overrideUrlOrEmpty(import.meta.env.VITE_VSIX_DOWNLOAD_URL_WIN32) ||
    cursorCdnUrl('win32-x64');
  const vsixUrlLinux =
    overrideUrlOrEmpty(import.meta.env.VITE_VSIX_DOWNLOAD_URL_LINUX) ||
    cursorCdnUrl('linux-x64');
  // Prefer a real artifact URL; the VITE_VSIX_DOWNLOAD_URL default is the
  // GitHub releases page, which is only useful as a last resort.
  const vsixUrlFallback =
    vsixUrlDarwin || overrideUrlOrEmpty(import.meta.env.VITE_VSIX_DOWNLOAD_URL);

  let vsixUrl = vsixUrlFallback;
  let actualPlatform: Platform | null = null;
  if (detectedPlatform === Platform.DARWIN && vsixUrlDarwin) {
    vsixUrl = vsixUrlDarwin;
    actualPlatform = Platform.DARWIN;
  } else if (detectedPlatform === Platform.WIN32 && vsixUrlWin32) {
    vsixUrl = vsixUrlWin32;
    actualPlatform = Platform.WIN32;
  } else if (detectedPlatform === Platform.LINUX && vsixUrlLinux) {
    vsixUrl = vsixUrlLinux;
    actualPlatform = Platform.LINUX;
  }

  const getInstallationInstruction = (): string => {
    if (detectedPlatform === Platform.DARWIN)
      return 'To install on macOS, press Cmd+Shift+P in Cursor and then select "Extensions: Install from VSIX"';
    if (detectedPlatform === Platform.WIN32)
      return 'To install on Windows, press Ctrl+Shift+P in Cursor and then select "Extensions: Install from VSIX"';
    if (detectedPlatform === Platform.LINUX)
      return 'To install on Linux, press Ctrl+Shift+P in Cursor and then select "Extensions: Install from VSIX"';
    return 'To install, press Cmd+Shift+P (macOS) or Ctrl+Shift+P (Windows/Linux) in Cursor and then select "Extensions: Install from VSIX"';
  };

  const getVSCodeInstallationInstruction = (): string => {
    if (detectedPlatform === Platform.DARWIN)
      return 'To install on macOS, press Cmd+Shift+P in VSCode and then select "Extensions: Install from VSIX"';
    if (detectedPlatform === Platform.WIN32)
      return 'To install on Windows, press Ctrl+Shift+P in VSCode and then select "Extensions: Install from VSIX"';
    if (detectedPlatform === Platform.LINUX)
      return 'To install on Linux, press Ctrl+Shift+P in VSCode and then select "Extensions: Install from VSIX"';
    return 'To install, press Cmd+Shift+P (macOS) or Ctrl+Shift+P (Windows/Linux) in VSCode and then select "Extensions: Install from VSIX"';
  };

  const vscodeCopilotUrl = vsixVersion
    ? `${VSCODE_COPILOT_BASE_URL}/v${vsixVersion}/kirin-vscode-copilot-v${vsixVersion}.vsix`
    : `${VSCODE_COPILOT_BASE_URL}/latest/kirin-vscode-copilot-latest.vsix`;
  const vscodeCopilotDownloadButtonText = vsixVersion
    ? `Download Kirin VSCode Copilot ${vsixVersion}`
    : 'Download Latest';

  const agentReleasedIds = useMemo(
    () => AGENT_PLATFORM_IDS.filter(id => released.has(id)),
    [released]
  );
  const agentHighlighted = useMemo(
    () => agentReleasedIds.map(id => PLATFORM_REGISTRY[id].displayName),
    [agentReleasedIds]
  );
  const agentComingSoon = useMemo(() => {
    // ponytail: static list auto-prunes once a tool gains a PlatformId, so it
    // can never double-list with the registry-derived COMING SOON entries.
    const known = new Set<string>(
      AGENT_PLATFORM_IDS.map(id => PLATFORM_REGISTRY[id].displayName)
    );
    return [
      ...AGENT_PLATFORM_IDS.filter(id => !agentReleasedIds.includes(id)).map(
        id => PLATFORM_REGISTRY[id].displayName
      ),
      ...AGENT_COMING_SOON_TOOLS.filter(label => !known.has(label)),
    ];
  }, [agentReleasedIds]);

  return (
    <div className="space-y-6">
      {/* Step 1: API Key */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Step 1: API Key Management
            {organizationId && (
              <span className="text-sm font-normal text-muted-foreground">
                (Personal Organization Key)
              </span>
            )}
          </CardTitle>
          <CardDescription>
            {organizationId
              ? 'Create and manage your personal organization API key for authenticating with Kirin. This key is specific to you and scoped to this organization.'
              : 'Create and manage your personal API key for authenticating with Kirin.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {organizationId ? (
              <OrganizationKeyControl organizationId={organizationId} />
            ) : (
              <PersonalKeyControl />
            )}
            {organizationId && isOwner && (
              <div className="text-sm text-muted-foreground">
                Are you looking for the MDM API key?{' '}
                <Link
                  to={`/${organizationId}/settings`}
                  className="text-primary hover:underline font-medium"
                >
                  Click here
                </Link>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Step 2 & 3: Install and Configure */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Step 2 & 3: Install & Configure
          </CardTitle>
          <CardDescription>
            Choose your AI coding tool and follow the complete setup.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <InstallCardSelector
            selected={effectiveCard}
            onSelect={setSelectedCard}
            showVSCode={showVSCode}
            agentToolIds={agentReleasedIds}
          />

          {effectiveCard === 'agent' && (
            <SetupFrame cardId="agent">
              <AgentCoverageBanner
                highlighted={agentHighlighted}
                comingSoon={agentComingSoon}
              />
              <AgentInstallSteps
                platformName="Kirin Agent"
                platformDownloadMeta={agentPlatformDownloadMeta}
                detectedPlatform={detectedPlatform}
                userArch={detectedArch}
              />
              {organizationId && released.has('claude_desktop') && (
                <ClaudeDesktopPluginSteps organizationId={organizationId} />
              )}
            </SetupFrame>
          )}

          {effectiveCard === 'cursor' && (
            <SetupFrame cardId="cursor">
              <AgentPrerequisiteBanner
                onInstallAgent={() => setSelectedCard('agent')}
              />
              <CursorInstallSteps detectedPlatform={detectedPlatform} />
            </SetupFrame>
          )}

          {showVSCode && effectiveCard === 'vscode' && (
            <SetupFrame cardId="vscode">
              <AgentPrerequisiteBanner
                onInstallAgent={() => setSelectedCard('agent')}
              />
              <VSCodeInstallSteps />
            </SetupFrame>
          )}
        </CardContent>
      </Card>

      {showVsixDownload && (
        <VsixDirectDownload
          vsixUrl={vsixUrl}
          vsixVersion={vsixVersion}
          actualPlatform={actualPlatform}
          vsixUrlFallback={vsixUrlFallback}
          platformDownloadMeta={[
            {
              platform: Platform.DARWIN,
              version: vsixVersion,
              url: vsixUrlDarwin,
            },
            {
              platform: Platform.WIN32,
              version: vsixVersion,
              url: vsixUrlWin32,
            },
            {
              platform: Platform.LINUX,
              version: vsixVersion,
              url: vsixUrlLinux,
            },
          ]}
          vscodeCopilotUrl={vscodeCopilotUrl}
          vscodeCopilotDownloadButtonText={vscodeCopilotDownloadButtonText}
          cursorInstallationInstruction={getInstallationInstruction()}
          vsCodeInstallationInstruction={getVSCodeInstallationInstruction()}
        />
      )}
    </div>
  );
}
